// ===========================================
// Consent Preferences Section
// Account Settings section for parents to view/update media exposure preference
// ===========================================

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cookie as CookieIcon,
  PhotoCamera as MediaIcon,
} from '@mui/icons-material';
import MediaExposureSelector, { MediaExposureDot } from '../../parent/MediaExposureSelector';
import { useUpdateMediaExposure } from '../../../hooks/useConsent';
import { useCookieConsent } from '../../../contexts/CookieConsentContext';
import type { MediaExposure } from '../../../api/consent.api';

// ===========================================
// TYPES
// ===========================================

interface Props {
  familyId: string;
  consentAccepted: boolean;
  consentAcceptedAt?: string | null;
  mediaExposure: MediaExposure;
  isFunMode?: boolean;
}

const EXPOSURE_LABELS: Record<MediaExposure, string> = {
  NO: 'No Media Use',
  BLUR: 'Blurred / Anonymised',
  FULL: 'Full Consent',
  PENDING: 'Not yet set',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// ===========================================
// COMPONENT
// ===========================================

export default function ConsentPreferencesSection({
  familyId,
  consentAccepted,
  consentAcceptedAt,
  mediaExposure,
  isFunMode = false,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<Exclude<MediaExposure, 'PENDING'> | null>(
    mediaExposure !== 'PENDING' ? (mediaExposure as Exclude<MediaExposure, 'PENDING'>) : null
  );
  const [saved, setSaved] = useState(false);
  const [cookieReset, setCookieReset] = useState(false);

  const updateExposure = useUpdateMediaExposure();
  const { state: cookieState, resetConsent } = useCookieConsent();

  const handleResetCookies = () => {
    resetConsent();
    setCookieReset(true);
    setTimeout(() => setCookieReset(false), 3000);
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      await updateExposure.mutateAsync({ familyId, mediaExposure: selected });
      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // error handled by mutation
    }
  };

  const handleCancel = () => {
    setSelected(
      mediaExposure !== 'PENDING' ? (mediaExposure as Exclude<MediaExposure, 'PENDING'>) : null
    );
    setIsEditing(false);
  };

  const cardSx = isFunMode
    ? {
        mb: 3,
        border: '2px solid #96DAC9',
        bgcolor: 'rgba(150, 218, 201, 0.08)',
        borderRadius: 3,
      }
    : { mb: 3 };

  return (
    <Card sx={cardSx}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Consent &amp; Preferences
        </Typography>

        {/* ── T&C / Privacy acknowledgement ── */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <CheckCircleIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Terms &amp; Conditions and Privacy Policy
          </Typography>
        </Stack>

        {!consentAccepted ? (
          <Alert severity="warning" sx={{ borderRadius: 1, mb: 2 }}>
            You have not yet agreed to the Terms &amp; Conditions and Privacy Policy. Reload the
            page to review and agree.
          </Alert>
        ) : (
          <Box sx={{ mb: 2, pl: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2">
                Terms &amp; Conditions accepted
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2">
                Privacy Policy accepted
              </Typography>
            </Stack>
            {consentAcceptedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Accepted on {formatDate(consentAcceptedAt)}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Cookie preferences ── */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <CookieIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Cookie Preferences
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {cookieState.hasConsented
            ? `Preferences last updated${cookieState.consentDate ? ` on ${formatDate(cookieState.consentDate)}` : ''}.`
            : 'You have not yet set your cookie preferences.'}
        </Typography>
        {cookieReset && (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 1 }}>
            Cookie preferences reset — the banner will reappear so you can update your choices.
          </Alert>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<CookieIcon />}
          onClick={handleResetCookies}
        >
          Update Cookie Preferences
        </Button>

        {consentAccepted && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* ── Media exposure ── */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <MediaIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                Media &amp; Marketing Consent
              </Typography>
            </Stack>

            {!isEditing && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <MediaExposureDot exposure={mediaExposure} />
                <Typography variant="body2" fontWeight={500}>
                  {EXPOSURE_LABELS[mediaExposure]}
                </Typography>
              </Stack>
            )}

            {saved && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }}>
                Media consent preference updated.
              </Alert>
            )}

            {isEditing ? (
              <>
                <MediaExposureSelector
                  value={selected}
                  onChange={setSelected}
                  disabled={updateExposure.isPending}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancel}
                    disabled={updateExposure.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={updateExposure.isPending || !selected}
                    startIcon={
                      updateExposure.isPending ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : undefined
                    }
                  >
                    {updateExposure.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </>
            ) : (
              <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
                Update Media Preference
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
